/**
 * The single loader for the Google Maps Places script.
 *
 * There were two: one in `RegisterPage` (driver town lookup) and one in
 * `EstimateWidget` (homepage route estimate), each with its own module-level
 * promise. Two modules meant two guards, so a visitor who used the homepage
 * estimate and then opened registration in the same session could have the
 * Maps script injected into <head> twice. One module, one promise, one script.
 *
 * The key is a browser-delivered Places key, so it is not a secret — but it must
 * be HTTP-referrer restricted in the Google console, and it comes from the
 * environment only. `RegisterPage` previously fell back to a key hardcoded in
 * source, which silently defeated env configuration and shipped a live key in
 * the repository.
 */

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

/**
 * `places` powers address autocomplete; `geometry` is needed to draw a route
 * line and fit bounds on a map. Both ride on one script load — requesting them
 * separately would mean a second injection, which is the bug this module was
 * created to remove.
 */
const LIBRARIES = 'places,geometry';

let scriptPromise: Promise<void> | null = null;

function alreadyLoaded(): boolean {
  const google = (window as any).google;
  return Boolean(google?.maps?.places?.AutocompleteService && google?.maps?.places?.PlacesService);
}

/** True once a map can be constructed — checked separately from Places. */
export function isMapsReady(): boolean {
  return Boolean((window as any).google?.maps?.Map);
}

export function ensureGooglePlacesLoaded(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Places needs a browser.'));
  }

  if (alreadyLoaded()) return Promise.resolve();

  if (!GOOGLE_PLACES_API_KEY) {
    return Promise.reject(new Error('Google Places is not configured.'));
  }

  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_PLACES_API_KEY,
    )}&libraries=${LIBRARIES}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      /* Let a later caller retry rather than caching the failure forever. */
      scriptPromise = null;
      reject(new Error('Could not load Google Places right now.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}
